import { SettingsRepository } from 'app/modules/settings/domain/repository/SettingsRepository';
import { AxiosTencerAdminApiClient } from 'app/shared/infrastructure/api/AxiosTencerAdminApiClient';
import { SettingName } from 'app/modules/settings/domain/valueObject/SettingName';
import { SettingsArrayMapper } from 'app/modules/settings/infrastructure/repository/api/dataMapper/SettingsArrayMapper';
import { SettingMapper } from 'app/modules/settings/infrastructure/repository/api/dataMapper/SettingMapper';
import { SettingsArray } from 'app/modules/settings/domain/entity/SettingsArray';
import { Setting } from 'app/modules/settings/domain/entity/Setting';
import { Price } from 'app/modules/orders/domain/value-objects/price';
import { PutSettingItemMapper } from 'app/modules/settings/infrastructure/repository/api/dataMapper/PutSettingItemMapper';
import { WarehouseContact } from 'app/modules/settings/domain/entity/WarehouseContact';
import { WarehouseContactMapper } from 'app/modules/settings/infrastructure/repository/api/dataMapper/WarehouseContactMapper';

export class ApiSettingsRepository implements SettingsRepository {

    constructor(private _apiClient: AxiosTencerAdminApiClient) {}

    public async findAll(): Promise<SettingsArray> {
        const data = await this._apiClient.getSettings();

        return SettingsArrayMapper.toDomain(data.data);
    }

    public async findByName(name:SettingName): Promise<Setting> {
        const settings = await this.findAll();
        const settingByName = settings.settings.find((setting) => setting.name.value === name.value);

        if (!settingByName) {
            throw new Error('Setting can not be undefined');
        }
        return settingByName;
    }

    public async findAllWarehouseContacts(): Promise<WarehouseContact[]> {
        const data = await this._apiClient.getWarehouseContacts();

        return data.data.map(WarehouseContactMapper.toDomain);
    }

    public async add(setting: Setting): Promise<string> {
        const result = await this._apiClient.putSettingItem(
            PutSettingItemMapper.toDomain(setting),
        );

        return result.data;
    }

    public async patchShipmentAppointment(
        insurance: number,
        cutOffHour: number,
        hoursAfterCutOffHour: number,
        hoursBeforeCutOffHour: number,
        shippingProcessingHoursToAdd: number,
        echoMaxWeightLbs?: number,
        echoMaxPallets?: number,
    ): Promise<void> {
        const result = await this._apiClient.patchShipmentAppointment({
            insurance,
            cut_off_hour: cutOffHour,
            hours_after_cut_off_hour: hoursAfterCutOffHour,
            hours_before_cut_off_hour: hoursBeforeCutOffHour,
            shipping_processing_hours_to_add: shippingProcessingHoursToAdd,
            shipment_echo_max_weight_lbs: echoMaxWeightLbs,
            shipment_echo_max_pallets: echoMaxPallets,
        });

        return result.data;
    }

    public async patchPickHandle(
        account: number,
        price: Price,
    ): Promise<void> {
        const result = await this._apiClient.patchPickHandle({
            price: price.value,
            account,
        });

        return result.data;
    }

    public async patchWarehouseContact(warehouseContact: WarehouseContact): Promise<void> {
        const result = await this._apiClient.patchWarehouseContact({
            code: warehouseContact.code.value,
            payload: {
                name: warehouseContact.name,
                phone_number: warehouseContact.phone.value,
            },
        });

        return result.data;
    }

    public async findSampleAccount(): Promise<Setting> {
        const data = await this._apiClient.getSampleAccount();
        const setting = SettingMapper.toDomain(data.data);
        if (!setting) {
            throw new Error('Sample account setting not found');
        }
        return setting;
    }

    public async patchSampleAccount(account: number): Promise<void> {
        const result = await this._apiClient.patchSampleAccount({
            value: account,
        });

        return result.data;
    }

    public async findSampleDiscountParams(): Promise<Setting[]> {
        const data = await this._apiClient.getSampleQuantityLimitDiscountPercent();
        return data.data
            .map((item) => SettingMapper.toDomain(item))
            .filter((setting): setting is Setting => setting !== null);
    }

    public async patchSampleDiscountParams(limit: number, percent: number): Promise<void> {
        const result = await this._apiClient.patchSampleQuantityLimitDiscountPercent({
            fullPriceQuantityLimit: limit,
            overLimitDiscountPercent: percent,
        });

        return result.data;
    }

}
